// src/pages/SalesReturnRecord/components/SalesReturnRecordFilters.js
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Card, Row, Col, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { setFilters } from "../SalesReturnRecordSlice";
import { fetchSalesReturnRecord } from "../SalesReturnRecordThunk";

import { useDefaultDateRange } from "../../../hooks/useDefaultDateRange";

import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";

import {
  fetchCustomers,
  fetchItems,
  fetchGroups,
  fetchCategories,
  fetchUsers,
  fetchTagGroups,
  fetchTagGroupFields,
  fetchLocations,
  fetchItemSerials,
} from "../../../store/commonThunks/lookups";
import { useSearchParams } from "react-router-dom";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";

const SalesReturnRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.salesReturnRecord);
  const lookups = useSelector((state) => state.lookups);

  const dynamicSpan = filters.filterType === "By Tag" ? 3 : 4;
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const dateFrom = searchParams.get("dateFrom");
    const dateTo = searchParams.get("dateTo");

    if (dateFrom && dateTo) {
      dispatch(setFilters({
        filterType: "All",
        recordType: "without item",
        fromDate: dateFrom,
        toDate: dateTo,
      }));
    }
  }, []);
  useDefaultDateRange(setFilters, filters, "thisMonth"); // 👈 defaults to today // using custom hook to default date

  // quick range handler

  useEffect(() => {
    dispatch(fetchGroups());
    dispatch(fetchCustomers());
    dispatch(fetchItems());
    dispatch(fetchCategories());
    dispatch(fetchUsers());
    dispatch(fetchTagGroups());
    dispatch(fetchLocations());
  }, [dispatch]);

  // Auto run report once defaults are in place
  useEffect(() => {
    if (
      filters.filterType &&
      filters.recordType &&
      filters.fromDate &&
      filters.toDate
    ) {
      handleReport();
    }
  }, [filters]);

  const handleFilterChange = (field, value) => {
    if (field === "filterType") {
      const autoWithoutItemTypes = [
        "By Item",
        "By Tag",
        "By Group",
        "By Category",
      ]

      dispatch(
        setFilters({
          filterType: value,
          recordType: autoWithoutItemTypes.includes(value)
            ? "without item"
            : filters.recordType,
          customerId: null,
          itemId: null,
          groupId: null,
          categoryId: null,
          userId: null,
          locationId: null,
          tag_groupnameid: null,
          tagFeildNameid: null,
        })
      );
      return;
    }
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    const payload = {
      selectedFilterType: filters.filterType,
      selectedRecordType: filters.recordType,
      filters: {
        ...filters,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      },
    };

    dispatch(fetchSalesReturnRecord(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  const renderExtraField = () => {
    switch (filters.filterType) {
      case "By Customer":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Customer</label>
            <SearchListAutoComplete
              entityType="customer"
              optionsList={lookups.customers.map((e) => ({
                sale_id: e.acc_id,
                display_text: e.acc_name,
              }))}
              onSelect={(obj) => handleFilterChange("customerId", obj?.sale_id)}
              placeholder="Customer"
              showAddNew={false}
              allowClear={true}
            />
          </Col>
        );

      case "By Item":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Item</label>
            <SearchListAutoComplete
              entityType="item"
              optionsList={lookups.items.map((e) => ({
                sale_id: e.item_id,
                display_text: e.item_name,
              }))}
              onSelect={(obj) => handleFilterChange("itemId", obj?.sale_id)}
              placeholder="Item"
              showAddNew={false}
              allowClear={true}
            />
          </Col>
        );

      case "By Group":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Group</label>
            <SearchListAutoComplete
              entityType="group"
              optionsList={lookups.groups.map((e) => ({
                sale_id: e.group_id,
                display_text: e.group_name,
              }))}
              onSelect={(obj) => handleFilterChange("groupId", obj?.sale_id)} // ✅ use sale_id
              placeholder="Group"
              showAddNew={false}
              allowClear={true}
            />
          </Col>
        );

      case "By Category":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Category</label>
            <SearchListAutoComplete
              entityType="category"
              optionsList={lookups.categories.map((e) => ({
                sale_id: e.category_id,
                display_text: e.category_name,
              }))}
              onSelect={(obj) => handleFilterChange("categoryId", obj?.sale_id)}
              placeholder="Category"
              showAddNew={false}
              allowClear={true}
            />
          </Col>
        );

      case "By User":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={lookups.users.map((e) => ({
                sale_id: e.id,
                display_text: e.full_name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="User"
              showAddNew={false}
              allowClear={true}
            />
          </Col>
        );

      case "By Tag":
        return (
          <>
            <Col xs={24} sm={12} md={8} xl={5}>
              <label>Tag Group</label>
              <SearchListAutoComplete
                optionsList={lookups.tagGroups.map((tg) => ({
                  sale_id: tg.id,
                  display_text: tg.tag_groupname,
                }))}
                onSelect={(obj) => {
                  handleFilterChange("tag_groupnameid", obj?.sale_id);
                  if (obj?.sale_id) dispatch(fetchTagGroupFields(obj.sale_id)); // ✅ just pass id
                }}
                placeholder="Tag Group"
                showAddNew={false}
              />
            </Col>
            <Col xs={24} sm={12} md={8} xl={5}>
              <label>Tag Field</label>
              <SearchListAutoComplete
                optionsList={lookups.tagFields.map((tf) => ({
                  sale_id: tf.id,
                  display_text: tf.display_text || tf.combinedField, // ✅ consistent naming
                }))}
                onSelect={(obj) =>
                  handleFilterChange("tagFeildNameid", obj?.sale_id)
                }
                placeholder="Tag Field"
                showAddNew={false}
              />
            </Col>
          </>
        );

      // case "By Item Serial":
      // return (
      //   <Col span={6}>
      //     <label>Search Item Serial</label>
      //     <SearchAutoComplete
      //       searchThunk={(text) => dispatch(fetchItems(text))} // ⚠️ If you have a dedicated fetchItemSerials thunk, use that
      //       resultSelector={(state) => state.lookups.items}
      //       onSelect={(obj) => handleFilterChange("itemSerial", obj?.serial_no)} // adapt to your API field
      //       placeholder="Item Serial"
      //     />
      //   </Col>
      // );

      case "By Location":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search Location</label>
            <SearchListAutoComplete
              key={filters?.filterType}
              optionsList={lookups?.locations?.map((loc) => ({
                sale_id: loc.location_id,
                display_text: loc.location_name,
              }))}
              onSelect={(obj) => handleFilterChange("locationId", obj?.sale_id)}
              placeholder="Location"
              showAddNew={false}
              allowClear={true}
            />
          </Col>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="product-replace-filters mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} wrap style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4} >
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Customer", display_text: "By Customer" },
              { sale_id: "By Item", display_text: "By Item" },
              { sale_id: "By Group", display_text: "By Group" },
              { sale_id: "By Category", display_text: "By Category" },
              { sale_id: "By Location", display_text: "By Location" },
              { sale_id: "By User", display_text: "By User" },
              // { sale_id: "By Item Serial", display_text: "By Item Serial" },
              // { sale_id: "By Tag", display_text: "By Tag" },
            ]}
            value={{
              value: filters.filterType, // 👈 ensure UI matches Redux
              label: filters.filterType,
            }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}
        {!["By Item", "By Tag", "By Group", "By Category"].includes(
          filters.filterType
        ) && (
            <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4} >
              <label>Record Type</label>
              <SearchListAutoComplete
                optionsList={[
                  { sale_id: "with item", display_text: "With Item" },
                  { sale_id: "without item", display_text: "Without Item" },
                ]}
                value={{
                  value: filters.recordType, // 👈 sync to Redux
                  label:
                    filters.recordType === "with item"
                      ? "With Item"
                      : "Without Item",
                }}
                onSelect={(obj) => handleFilterChange("recordType", obj?.sale_id)}
                placeholder="Record Type"
                showAddNew={false}
              />
            </Col>
          )}

        {/* ⬇️ Reusable Date Filter */}
        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />

        {/* Report Button */}
        <Col
          xs={24} sm={12} md={9} xl={3}
          style={{ paddingTop: "1.4rem" }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            style={{height: 35.19}}
            block
          >
            Submit
          </Button>
        </Col>
      </Row>
      <style>{`
    @media (min-width: 768px) and (max-width: 1024px) {
      .product-replace-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
  `}</style>
    </Card>
  );
};

export default SalesReturnRecordFilters;
